import { useMemo, useState } from 'react';
import { Button, Card, Descriptions, Form, Input, Modal, Select, Space, Spin, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAddRiskMeasure, useRiskCards, useRiskMeasures } from '@/entities/risk/api/hooks';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import type { RiskMeasure } from '@/entities/risk/model/riskSchema';
import type { Status } from '@/entities/user/model/userSchema';
import { measureStatusLabel } from '../constants';
import { computeRisksAnalytics, isOverdue } from '../utils';
import s from './MeasuresTab.module.scss';

export function MeasuresTab() {
  const { data: measures, isLoading: measuresLoading } = useRiskMeasures();
  const { data: cards } = useRiskCards();
  const addMeasure = useAddRiskMeasure();

  const [measureModal, setMeasureModal] = useState(false);
  const [measureFilter, setMeasureFilter] = useState<string>('all');
  const [measureForm] = Form.useForm();
  const [now] = useState(Date.now);

  const allMeasures = useMemo(() => measures ?? [], [measures]);
  const allCards = useMemo(() => cards ?? [], [cards]);

  const analytics = useMemo(() => computeRisksAnalytics(allCards, allMeasures, now), [allCards, allMeasures, now]);

  const filteredMeasures = useMemo(() => {
    if (measureFilter === 'done') return allMeasures.filter((m) => m.status === 'ok');
    if (measureFilter === 'active') return allMeasures.filter((m) => m.status !== 'ok');
    if (measureFilter === 'overdue') return allMeasures.filter((m) => isOverdue(m.deadline) && m.status !== 'ok');
    return allMeasures;
  }, [allMeasures, measureFilter]);

  const measureColumns: ColumnsType<RiskMeasure> = useMemo(
    () => [
      { title: 'Мероприятие', dataIndex: 'title', key: 'title' },
      { title: 'Рабочее место', dataIndex: 'workplaceId', key: 'wp' },
      { title: 'Ответственный', dataIndex: 'responsible', key: 'resp' },
      {
        title: 'Срок',
        dataIndex: 'deadline',
        key: 'deadline',
        render: (d: string, r: RiskMeasure) => {
          const overdue = isOverdue(d) && r.status !== 'ok';
          return <span style={overdue ? { color: '#cf1322', fontWeight: 600 } : undefined}>{d}</span>;
        },
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        render: (st: Status, r: RiskMeasure) => {
          const actualStatus = isOverdue(r.deadline) && st !== 'ok' ? ('err' as Status) : st;
          return <StatusBadge status={actualStatus} label={measureStatusLabel[actualStatus] ?? st} />;
        },
      },
    ],
    [],
  );

  const submitMeasure = async () => {
    const v = await measureForm.validateFields();
    addMeasure.mutate(
      { ...v, status: 'warn' as Status },
      {
        onSuccess: () => {
          setMeasureModal(false);
          message.success('Мероприятие добавлено');
        },
      },
    );
  };

  return (
    <>
      <Card
        size="small"
        title={<span className={s['root__card-title']}>Мероприятия по снижению рисков</span>}
        extra={
          <Button
            type="primary"
            onClick={() => {
              measureForm.resetFields();
              setMeasureModal(true);
            }}
          >
            + Мероприятие
          </Button>
        }
      >
        <Space className={s['root__measure-filter-row']}>
          <span className={s['root__measure-filter-label']}>Показать:</span>
          <Select
            value={measureFilter}
            onChange={setMeasureFilter}
            className={s['root__measure-filter-select']}
            options={[
              { value: 'all', label: 'Все мероприятия' },
              { value: 'active', label: 'В работе' },
              { value: 'done', label: 'Выполнено' },
              { value: 'overdue', label: `Просроченные (${analytics.overdue})` },
            ]}
          />
        </Space>
        <Spin spinning={measuresLoading}>
          <Table
            dataSource={filteredMeasures}
            columns={measureColumns}
            rowKey="id"
            size="small"
            pagination={{ ...antTablePagination }}
            scroll={antTableScroll}
          />
        </Spin>
      </Card>

      <Card
        size="small"
        className={s['root__analytics-card']}
        title={<span className={s['root__card-title']}>Аналитика мероприятий</span>}
      >
        <Descriptions column={2} size="small" colon={false}>
          <Descriptions.Item label="Всего мероприятий">{analytics.total}</Descriptions.Item>
          <Descriptions.Item label="Выполнено">
            <span style={{ color: '#389e0d' }}>{analytics.done}</span>
          </Descriptions.Item>
          <Descriptions.Item label="В работе">{analytics.inProgress}</Descriptions.Item>
          <Descriptions.Item label="Просрочено">
            <span style={analytics.overdue > 0 ? { color: '#cf1322', fontWeight: 600 } : undefined}>
              {analytics.overdue}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Эффективность">
            {analytics.total > 0 ? `${Math.round((analytics.done / analytics.total) * 100)}%` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Карт на пересмотре">
            <span style={analytics.reviewOverdue > 0 ? { color: '#cf1322', fontWeight: 600 } : undefined}>
              {analytics.reviewOverdue} просрочено, {analytics.reviewSoon} скоро
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        title="Новое мероприятие"
        open={measureModal}
        onCancel={() => setMeasureModal(false)}
        onOk={submitMeasure}
        okText="Сохранить"
        confirmLoading={addMeasure.isPending}
        destroyOnClose
      >
        <Form form={measureForm} layout="vertical">
          <Form.Item name="title" label="Мероприятие" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="workplaceId" label="Рабочее место" rules={[{ required: true }]}>
            <Input placeholder="РМ-001" />
          </Form.Item>
          <Form.Item name="responsible" label="Ответственный" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="deadline" label="Срок" rules={[{ required: true }]}>
            <Input placeholder="ДД.ММ.ГГГГ" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
